import _ from 'lodash';
import {
	Autocomplete,
	AutocompleteItem,
	Button,
	Divider,
	Input,
	Listbox,
	ListboxItem,
	Popover,
	PopoverContent,
	PopoverTrigger,
	Tab,
	Tabs,
	useDisclosure
} from "@heroui/react";
import {useInfiniteQuery, useQuery, useQueryClient} from '@tanstack/react-query';
import repositories from '../../repositories.jsx';
import {useGlobalAudioPlayer} from 'react-use-audio-player';
import PageLoader from '../../components/PageLoader.jsx';
import {useEffect, useMemo, useRef, useState} from 'react';
import EmptyProjects from '../../components/EmptyProjects.jsx';
import {useDebouncedCallback, useDebouncedState, useIntersection, useViewportSize,} from '@mantine/hooks';
import {useForm} from '@mantine/form';
import VoiceCard from '../../widgets/VoiceCard.jsx';
import Header from '../../components/Header.jsx';
import toast from 'react-hot-toast';
import {useVirtualizer} from '@tanstack/react-virtual';
import {useLocation} from "react-router-dom";

export default function Voices({search = null}) {
	const {load, isReady, isLoading, stop, playing, src, setVolume} = useGlobalAudioPlayer();
	const {width, height} = useViewportSize()
	const {isOpen, onOpenChange, onOpen, onClose} = useDisclosure()
	const {ref, entry} = useIntersection({
		threshold: 0.2,
	});
	const queryClient = useQueryClient()
	const [selectedKeys, setSelectedKeys] = useState(new Set([]));
	const [searchLanguage, setSearchLanguage] = useDebouncedState('', 500);
	const [voiceFilter, setVoiceFilter] = useState('all');
	const location = useLocation();
	const searchParams = new URLSearchParams(location.search);
	const queryType = searchParams.get("type");

	const form = useForm({
		initialValues: {
			search: '',
			provider: '',
			language: [],
			per_page: 28,
		}
	})
	
	const voiceList = useInfiniteQuery({
		queryKey: ['myVoiceList', voiceFilter],
		queryFn: ({pageParam}) => repositories.project.voiceList(_.merge(form.values, {
			page: pageParam,
			key: form.values.search,
			own_key: voiceFilter == 'own' && voiceFilter,
		})),
		initialPageParam: 1,
		getNextPageParam: (lastPage, pages, lastPageParam) => lastPage?.rows?.length > 27 ? lastPageParam + 1 : null,
	})
	
	const voiceSuggestions = useQuery({
		queryKey: ['voiceSuggestions'],
		queryFn: repositories.project.voiceSuggestions
	})
	
	const allLanguages = useQuery({
		queryKey: ['allLanguages'],
		queryFn: repositories.allLanguages
	})
	
	const handleSearch = useDebouncedCallback(async () => {
		//await queryClient.resetQueries(['voiceList'])
		setVolume(0.1)
		await voiceList.refetch()
		stop();
		setVolume(1)
	}, 1000);
	
	form.watch('search', () => {
		if (voiceList.isFetched) {
			handleSearch()
		}
	})
	
	form.watch('language', () => {
		if (voiceList.isFetched) {
			handleSearch()
		}
	})
	
	const playAudio = async (src) => {
		if (!_.startsWith(src, 'http') || !_.endsWith(src, '.mp3')) {
			toast.error('Invalid audio file')
			return;
		}
		stop();
		await load(src, {
			autoplay: true,
			format: 'mp3',
		});
	}
	
	useEffect(() => {
		setTimeout(() => {
			voiceList?.hasNextPage && voiceList.isFetched && entry?.isIntersecting && voiceList.fetchNextPage()
		}, 1000)
	}, [entry?.isIntersecting]);
	
	const allVoices = useMemo(() => {
		return _.flatMap(voiceList.data?.pages, (item) => item?.rows || []);
	}, [voiceList.data])
	
	const inputWrapper = 'min-h-12 min-w-[50vw] lg:min-w-[300px] bg-default-500/5 border-1 data-[focus=true]:!border-primary data-[hover=true]:border-primary shadow-none';
	
	const visualizerRootRef = useRef(null)
	
	const rowVisualizer = useVirtualizer({
		count: _.size(allVoices || []),
		getScrollElement: () => visualizerRootRef.current,
		estimateSize: () => 110,
		overscan: 5
	})
	
	function filterArrayContent(array, query, accessor = (item) => item) {
		if (!query) return array;
		
		return _.filter(array, (item) => {
			const content = _.toLower(_.toString(accessor(item)));
			return content.includes(_.toLower(query));
		});
	}
	
	const filterLanguages = useMemo(() => {
		return filterArrayContent(allLanguages?.data || [], searchLanguage, (item) => item?.language) || []
	}, [searchLanguage, allLanguages.data]);
	
	const autoCompleteItems = useMemo(() => {
		const data = []
		if (_.size(data) < 1) {
			data.push({
				label: 'Male',
				value: 'male',
			})
			data.push({
				label: 'Female',
				value: 'female',
			})
			for (const item of allVoices) {
				data.push({label: item?.speaker_name, value: _.lowerCase(item?.speaker_name)})
			}
		}
		return _.uniqBy(data, 'label') || []
	}, [allVoices])

	useEffect(() => {
		if(queryType === 'my_voice') {
			setVoiceFilter('own')
		}
	},[])

	return (
		<div className="relative">
			<Header currentPage="voices"/>
			<div className="flex flex-wrap justify-between items-center mb-5 gap-3">
				<Tabs
					selectedKey={voiceFilter}
					onSelectionChange={setVoiceFilter}
					radius="full"
					classNames={{
						tabList: 'bg-slate-100/80 p-1 rounded-full gap-1',
						cursor: '!bg-gradient-to-r !from-primary !to-fuchsia-500 shadow-md',
						tab: 'px-5 h-10',
						tabContent: 'font-semibold text-slate-500 group-data-[selected=true]:text-white',
					}}>
					<Tab key="all" title="All Voices"/>
					<Tab key="own" title={<span className="flex items-center gap-2"><i className="hgi-stroke hgi-star text-base"/>My Voices</span>}/>
				</Tabs>
				<div className="flex justify-between items-center gap-3">
					<div className="hidden md:block w-full">
						<Autocomplete
							className="max-w-md w-[280px]"
							onSelectionChange={(value) => form.setFieldValue('search', value)}
							radius="full"
							variant="flat"
							aria-label="Search voices"
							aria-labelledby="Search voices"
							placeholder="Search voices ..."
							startContent={<i className="hgi-stroke hgi-search-list-01 text-slate-400 text-lg"/>}
							inputProps={{
								classNames: {
									inputWrapper: 'h-11 bg-slate-100/70 shadow-none border border-transparent data-[hover=true]:bg-slate-100 group-data-[focus=true]:!bg-white group-data-[focus=true]:!border-primary/40 group-data-[focus=true]:ring-4 group-data-[focus=true]:ring-primary/10 transition-colors',
								},
							}}
						>
							{voiceSuggestions.data?.suggestions?.map((item) => (
								<AutocompleteItem key={_.snakeCase(item)}>
									{item}
								</AutocompleteItem>
							))}
						</Autocomplete>
					</div>
					<div className="relative">
						<Popover
							showArrow offset={20}
							classNames={{
								content: 'max-w-64'
							}}
							placement="bottom-end">
							<PopoverTrigger>
								<Button
									isLoading={allLanguages.isLoading}
									onPress={(e) => {
										isOpen ? onClose() : onOpen()
										e.shouldStopPropagation = true
									}}
									variant="bordered"
									radius="full"
									className="h-11 px-5 min-w-[140px] font-semibold border-slate-200 bg-white text-slate-700 data-[hover=true]:border-primary data-[hover=true]:text-primary transition-colors"
									startContent={<i className="hgi-stroke hgi-preference-horizontal text-lg"></i>}
									endContent={!_.isEmpty(form.values.language) &&
										<span className="grid place-items-center min-w-5 h-5 px-1.5 rounded-full bg-primary text-white text-[11px] font-bold leading-none">
											{selectedKeys.size || _.split(form.values.language, ', ').filter(Boolean).length}
										</span>
									}>
									Language
								</Button>
							</PopoverTrigger>
							<PopoverContent>
								<div className="py-2 w-full">
									<Input
										fullWidth
										label="Search language"
										placeholder="search ..."
										labelPlacement="outside"
										defaultValue={searchLanguage}
										onValueChange={setSearchLanguage}
										classNames={{
											label: 'text-slate-300'
										}}
										type="text"
									/>
								</div>
								<Divider className="my-1"/>
								<Listbox
									isVirtualized
									selectionMode="multiple"
									selectedKeys={selectedKeys}
									onSelectionChange={(keys) => {
										form.setFieldValue('language', _.cloneDeep(_.toArray(keys)?.join(', ')))
										setSelectedKeys(keys)
									}}
									label="Select from 1000 items"
									placeholder="Select..."
									virtualization={{
										maxListboxHeight: 300,
										itemHeight: 48,
									}}
									className="listBoxScrollHide"
									classNames={{
										base: '!scrollbar-hide',
										list: 'min-w-56 !scrollbar-hide'
									}}
									items={filterLanguages || []}
								>
									{(item, index) => (
										<ListboxItem
											key={item?.language || 'Unknown'}
											textValue={item?.language || 'Unknown'}
										>
											{item?.language || 'Unknown'}
										</ListboxItem>
									)}
								</Listbox>
								{!_.isEmpty(form.values.language) &&
									<Button
										className="w-full !cursor-pointer mt-1 font-semibold" variant="flat" radius="full" size="sm"
										startContent={<i className="hgi-stroke hgi-cancel-01 text-base"/>}
										onPress={() => {
											setSearchLanguage('')
											setSelectedKeys(new Set([]))
											form.setFieldValue('language', '')
										}}
										color="danger">
										Clear filter
									</Button>
								}
							</PopoverContent>
						</Popover>
					</div>
				</div>
			</div>
			{/*{!_.isEmpty(form.values.language) || searchDebouncedValue &&*/}
			{/*	<div className="flex gap-3 items-center mb-5">*/}
			{/*		<h2 className="font-sans font-medium text-xl">Filters:</h2>*/}
			{/*		<div className="flex justify-start gap-3 rounded-20 py-2 px-3 bg-white w-full">*/}
			{/*			{searchDebouncedValue && <Chip>{searchDebouncedValue}</Chip>}*/}
			{/*			{!_.isEmpty(form.values.language) && _.split(form.values.language, ', ')?.map((item, itemKey) => (*/}
			{/*				<Chip key={itemKey}>{item}</Chip>*/}
			{/*			))}*/}
			{/*		</div>*/}
			{/*	</div>*/}
			{/*}*/}
			
			<div className="md:hidden mb-5 px-3">
				<div className=" w-full">
					<Input
						id="firstName"
						variant="bordered"
						classNames={{inputWrapper}}
						{...form.getInputProps('search')}
						inputMode="search"
						placeholder="Search here..."
						startContent={<>
							<Button isLoading={voiceList.isLoading || voiceList.isFetching} isIconOnly variant=""
							        className="p-0">
								<i className={`hgi-stroke hgi-search-list-01 text-slate-500 text-lg`}/>
							</Button>
						</>}
						endContent={
							!_.isEmpty(form.values.search) &&
							<>
								<Button onPress={() => form.setFieldValue('search', '')} isIconOnly variant="light"
								        color="danger" className="!scale-85">
									<i className="hgi-stroke hgi-cancel-01 text-lg"/>
								</Button>
							</>}
					/>
				</div>
			</div>
			<div
				style={{
					height: `80dvh`,
					maxHeight: `${height - 220}px`,
				}}
				ref={visualizerRootRef}
				className="overflow-y-auto p-2 scrollbar-hide">
				{(voiceList?.isLoading || voiceList?.isFetching) &&
					<div className="w-full h-full top-0 left-0 fixed z-50 transition-all duration-300">
						<PageLoader/>
					</div>
					
				}
				
				{voiceList?.isSuccess && _.size(allVoices) > 0 &&
					<>
						<div className="grid grid-cols-1 md:grid-cols-3 2xl:grid-cols-4 gap-4 pb-10 ">
							{allVoices.map((item, index) => (
								<VoiceCard
									key={index}
									playAudio={playAudio}
									data={item}
								/>
							))}
						</div>
						{voiceList.hasNextPage &&
							<div ref={ref} className="w-full flex justify-center items-center py-4">
								<PageLoader size="md"/>
							</div>
						}
					</>
				}
				
				{voiceList?.isSuccess && _.size(allVoices) <= 0 &&
					<div className="rounded-20 bg-white p-6">
						<div
							className="text-center flex-grow h-[initial] border-dashed border-2  overflow-hidden border-slate-500/30 rounded-20 cursor-pointer flex flex-col items-center justify-center">
							<EmptyProjects
								icon={_.isEmpty(form.values.provider || form.values.search) ?
									<img src="/images/no-result.jpg" alt="upload-icon"
									     className="max-w-32 mx-auto mb-5 grayscale"/> : null}
								title={_.isEmpty(form.values.provider || form.values.search) ? 'No Voices Available' : 'No Matching Results'}
								children={_.isEmpty(form.values.provider || form.values.search) ? 'It looks like there are no voice listings yet' : 'We couldn’t find any voices that match your search. Try refining your search terms or check back later for new additions.'}
								className="bg-white bg-[url()] w-full h-full min-h-[600px] py-32"
							/>
						</div>
					</div>
				}
			</div>
		</div>
	)
}
